import React from 'react';
import {AvForm, AvGroup} from 'availity-reactstrap-validation';
import {Button, Col, Row} from 'reactstrap';
import {Fieldset} from 'primereact/fieldset';
import {toast} from 'react-toastify';
import GeneralUtils from '../../shared/utils/GeneralUtils';
import PurchaseRequisitionService from '../../shared/service/PurchaseRequisitionService';
import LicenseService from '../../shared/service/LicenseService';
import DataTableWithButton from '../../shared/component/dataTable/DataTableWithButton';
import PurchaseRequisitionMobileColumn from '../../shared/component/dataTable/columns/PurchaseRequisitionMobileColumn.json';
import AvField from '../../shared/component/form/AvField';
import Loading from '../../shared/component/others/Loading';
import {getRequisitionDocLabels} from '../purchaserequisition/PurchaseRequisitionUtils';
import {resolvePurchaseRequisitionStatusFromQuery} from '../dasboard/dashboardMenuUtils';
import {rememberMobileListReturnHash} from '../purchaserequisition/PurchaseRequisitionListNav';
import '../../shared/styles/SettingsPage.css';

const LICENSE_REQUIRED_MESSAGE = "You don't have an invoice license.";

const STATUS_FILTER_OPTIONS = [
    {name: 'Requisition', value: 'OPEN'},
    {name: 'Delivery Note', value: 'DELIVERY_NOTE'},
    {name: 'Return Note', value: 'RETURN'},
];

const MOBILE_LIST_STATUSES = new Set(['OPEN', 'DELIVERY_NOTE', 'RETURN']);

const STATUS_LABEL = {
    OPEN: 'Requisition',
    PURCHASE_ORDER: 'Purchase Order',
    DELIVERY_NOTE: 'Delivery Note',
    RETURN: 'Return Note',
    CLOSED: 'Closed'
};

const MOBILE_STATUS_FILTER_KEY = 'inventoryManagementMobile.statusFilter';

function readStatusQueryFromHash() {
    const hash = window.location.hash || '';
    const queryPart = hash.includes('?') ? hash.substring(hash.indexOf('?') + 1) : '';
    const resolved = resolvePurchaseRequisitionStatusFromQuery(queryPart);
    if (resolved && MOBILE_LIST_STATUSES.has(resolved)) {
        return resolved;
    }
    return null;
}

function loadStatusFilter(lockedStatus) {
    if (lockedStatus) {
        return {statusFilter: lockedStatus, statusLocked: true};
    }
    let statusFilter = 'OPEN';
    try {
        const saved = sessionStorage.getItem(MOBILE_STATUS_FILTER_KEY);
        if (saved != null && STATUS_FILTER_OPTIONS.some(option => option.value === saved)) {
            statusFilter = saved;
        }
    } catch (e) {
        statusFilter = 'OPEN';
    }
    return {statusFilter, statusLocked: false};
}

export default class PurchaseRequisitionMobile extends React.Component {
    constructor(props) {
        super(props);
        const lockedStatus = readStatusQueryFromHash();
        const stored = loadStatusFilter(lockedStatus);
        this.state = {
            hasInvoiceLicense: false,
            loading: false,
            requisitionList: [],
            allRequisitions: [],
            statusFilter: stored.statusFilter,
            statusLocked: stored.statusLocked
        };
        this.purchaseRequisitionService = new PurchaseRequisitionService();
        this.licenseService = new LicenseService();
    }

    componentDidMount() {
        if (!GeneralUtils.isMobile()) {
            window.location.href = '#';
            return;
        }
        if (this.state.statusLocked) {
            rememberMobileListReturnHash(this.state.statusFilter, true);
        }
        window.addEventListener('hashchange', this.syncStatusFromHash);
        this.checkInvoiceLicense();
    }

    checkInvoiceLicense = () => {
        this.licenseService.getLicenses(localStorage.getItem('merchantBranchId')).then(response => {
            if (!GeneralUtils.isNullOrEmpty(response) && GeneralUtils.isNullOrEmpty(response.type)
                && response.hasInvoiceLicense === true) {
                this.setState({hasInvoiceLicense: true}, () => this.loadOpenRequisitions());
            } else {
                toast.warn(LICENSE_REQUIRED_MESSAGE);
            }
        });
    };

    componentWillUnmount() {
        window.removeEventListener('hashchange', this.syncStatusFromHash);
    }

    syncStatusFromHash = () => {
        const lockedStatus = readStatusQueryFromHash();
        const next = loadStatusFilter(lockedStatus);
        if (next.statusLocked) {
            rememberMobileListReturnHash(next.statusFilter, true);
        }
        this.setState({
            statusFilter: next.statusFilter,
            statusLocked: next.statusLocked
        }, () => this.applyStatusFilter());
    };

    loadOpenRequisitions = () => {
        if (!this.state.hasInvoiceLicense) {
            return;
        }
        this.setState({loading: true});
        this.purchaseRequisitionService.findAllOpen().then(response => {
            if (!GeneralUtils.isNullOrEmpty(response) && GeneralUtils.isNullOrEmpty(response.type)) {
                const list = (response || []).map(item => ({
                    ...item,
                    // Keep raw ISO createDate — DataTable dateCell formats via moment (DD/MM/YYYY pre-format → Invalid date).
                    statusCode: item.requisitionStatus,
                    requisitionStatus: STATUS_LABEL[item.requisitionStatus] || item.requisitionStatus || '',
                    approved: item.approved === true || item.approved === 'true'
                }));
                this.setState({
                    allRequisitions: list,
                    loading: false
                }, () => this.applyStatusFilter());
            } else {
                this.setState({allRequisitions: [], requisitionList: [], loading: false});
            }
        }).catch(() => this.setState({loading: false}));
    };

    applyStatusFilter = () => {
        const {allRequisitions, statusFilter} = this.state;
        const selected = statusFilter || 'OPEN';
        const filtered = (allRequisitions || []).filter(item =>
            MOBILE_LIST_STATUSES.has(item.statusCode) && item.statusCode === selected
        );
        this.setState({requisitionList: filtered});
    };

    __handleStatusFilterChange = (e) => {
        if (this.state.statusLocked) {
            return;
        }
        const value = e.target.parsedValue !== undefined ? e.target.parsedValue : e.target.value;
        try {
            sessionStorage.setItem(MOBILE_STATUS_FILTER_KEY, value == null ? '' : String(value));
        } catch (err) {
            // ignore
        }
        this.setState({statusFilter: value}, () => this.applyStatusFilter());
    };

    rememberReturnAndGo = (href) => {
        rememberMobileListReturnHash(this.state.statusFilter, this.state.statusLocked);
        window.location.href = href;
    };

    openNewRequisition = () => {
        if (!this.state.hasInvoiceLicense) {
            return toast.warn(LICENSE_REQUIRED_MESSAGE);
        }
        this.rememberReturnAndGo('#inventoryManagementSaveMobile');
    };

    openNewDeliveryNote = () => {
        if (!this.state.hasInvoiceLicense) {
            return toast.warn(LICENSE_REQUIRED_MESSAGE);
        }
        this.rememberReturnAndGo('#inventoryManagementSaveMobile?-1&mode=delivery');
    };

    openNewReturn = () => {
        if (!this.state.hasInvoiceLicense) {
            return toast.warn(LICENSE_REQUIRED_MESSAGE);
        }
        this.rememberReturnAndGo('#inventoryManagementSaveMobile?-1&mode=returnNote');
    };

    onDataSelect = (data) => {
        if (!this.state.hasInvoiceLicense) {
            return toast.warn(LICENSE_REQUIRED_MESSAGE);
        }
        if (data?.id) {
            this.rememberReturnAndGo('#inventoryManagementSaveMobile?' + data.id);
        }
    };

    getListFields = () => {
        const {docNoLabel} = getRequisitionDocLabels(this.state.statusFilter, {forMobile: true});
        return PurchaseRequisitionMobileColumn.fields.map(field => {
            if (field.field === 'requisitionNo') {
                return {...field, header: docNoLabel};
            }
            return field;
        });
    };

    render() {
        if (!GeneralUtils.isMobile()) {
            return null;
        }

        const {docTitle} = getRequisitionDocLabels(this.state.statusFilter, {forMobile: true});
        const listLegend = `${docTitle} List`;

        return (
            <div>
                <AvForm onSubmit={(e) => e.preventDefault()}>
                    <Fieldset legend={listLegend}>
                        <Row className="align-items-center" style={{marginBottom: '8px'}}>
                            <Col>
                                {!this.state.statusLocked &&
                                    <AvGroup className="mb-2">
                                        <AvField
                                            type="select"
                                            className="form-control"
                                            name="statusFilter"
                                            value={this.state.statusFilter}
                                            onChange={this.__handleStatusFilterChange}
                                        >
                                            {STATUS_FILTER_OPTIONS.map(option => (
                                                <option value={option.value} key={option.name}>
                                                    {option.name}
                                                </option>
                                            ))}
                                        </AvField>
                                    </AvGroup>
                                }
                                {this.state.hasInvoiceLicense && this.state.statusFilter === 'OPEN' &&
                                    <Button
                                        type="button"
                                        color="outline-primary"
                                        className="settings-page__btn--add"
                                        style={{width: '100%', minHeight: '32px'}}
                                        onClick={this.openNewRequisition}
                                    >
                                        <i className="pi pi-plus" aria-hidden="true"/>
                                        Create Requisition
                                    </Button>
                                }
                                {this.state.hasInvoiceLicense && this.state.statusFilter === 'DELIVERY_NOTE' &&
                                    <Button
                                        type="button"
                                        color="outline-primary"
                                        className="settings-page__btn--add"
                                        style={{width: '100%', minHeight: '32px'}}
                                        onClick={this.openNewDeliveryNote}
                                    >
                                        <i className="pi pi-plus" aria-hidden="true"/>
                                        Create Delivery Note
                                    </Button>
                                }
                                {this.state.hasInvoiceLicense && this.state.statusFilter === 'RETURN' &&
                                    <Button
                                        type="button"
                                        color="outline-primary"
                                        className="settings-page__btn--add"
                                        style={{width: '100%', minHeight: '32px'}}
                                        onClick={this.openNewReturn}
                                    >
                                        <i className="pi pi-plus" aria-hidden="true"/>
                                        Create Return Note
                                    </Button>
                                }
                            </Col>
                        </Row>

                        {this.state.hasInvoiceLicense &&
                            <DataTableWithButton
                                ref={(elem) => this.refDataTable = elem}
                                fields={this.getListFields()}
                                objectName={PurchaseRequisitionMobileColumn.name}
                                data={this.state.requisitionList}
                                selectionMode="single"
                                paginator={true}
                                rows={50}
                                onDataSelect={this.onDataSelect}
                                loading={this.state.loading}
                            />
                        }
                    </Fieldset>
                </AvForm>
                {this.state.loading && <Loading/>}
            </div>
        );
    }
}
